import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase/client';
import { useAuthStore } from '../../stores/auth-store';
import type { SearchMode, UnifiedSearchResponse, CategoryFilters } from './types';

/**
 * Core hook for the unified search RPC.
 * Handles typeahead, overview, and category-level search.
 */
export function useUnifiedSearch(
    query: string,
    mode: SearchMode,
    filters?: Partial<CategoryFilters>,
    enabled: boolean = true
) {
    const { user } = useAuthStore();

    return useQuery({
        queryKey: ['unified-search', query, mode, filters],
        queryFn: async (): Promise<UnifiedSearchResponse> => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const { data, error } = await (supabase.rpc as any)('unified_search', {
                _query: query,
                _mode: mode,
                _user_id: user?.id || null,
                _page_size: filters?.pageSize || (mode === 'typeahead' ? 5 : mode === 'overview' ? 5 : 20),
                _cursor_id: null,
                _cursor_score: null,
                _country_id: filters?.countryId || 0,
                _place_of_birth: filters?.birthPlace || null,
                _place_of_passing: filters?.passingPlace || null,
                _min_candles: filters?.minCandles || 0,
                _max_candles: filters?.maxCandles || 0,
                _min_members: filters?.minMembers || 0,
                _max_members: filters?.maxMembers || 0,
                _sort_by: filters?.sortBy || 'relevance',
                _sort_order: filters?.sortOrder || 'DESC',
                _page: filters?.page || 1,
            });

            if (error) throw error;

            // The RPC returns a jsonb object; Supabase client may type it differently
            return data as unknown as UnifiedSearchResponse;
        },
        enabled: enabled && query.length >= 3,
        staleTime: 1000 * 60 * 2, // 2 minutes
        gcTime: 1000 * 60 * 5,    // 5 minutes garbage collection
    });
}

/**
 * Hook specifically for the typeahead dropdown.
 * Lightweight, fast, limited results.
 */
export function useTypeaheadSearch(query: string, enabled: boolean = true) {
    return useUnifiedSearch(query, 'typeahead', undefined, enabled);
}

/**
 * Hook for the search overview page.
 */
export function useOverviewSearch(query: string, enabled: boolean = true) {
    return useUnifiedSearch(query, 'overview', undefined, enabled);
}

/**
 * Hook for the paginated pages category search.
 */
export function useCategoryPagesSearch(query: string, filters: Partial<CategoryFilters>, enabled: boolean = true) {
    return useUnifiedSearch(query, 'category_pages', filters, enabled);
}

/**
 * Hook for the paginated tags category search.
 */
export function useCategoryTagsSearch(query: string, filters: Partial<CategoryFilters>, enabled: boolean = true) {
    return useUnifiedSearch(query, 'category_tags', filters, enabled);
}
